import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { BookingRequestReadStatusEnum, StatusByProviderEnum, StatusByRepEnum } from '../../../api';
import { useCurrencyFormatter } from '../../hooks/use-currency-formatter';
import { SortDataPayload } from '../../models/sort';
import { Avatar } from '../Avatar';
import { BookingRequestsFiltersState } from '../BookingRequestsFilters';
import { BookingRequestStatusBadge } from '../BookingRequestStatusBadge';
import { HederCell } from '../ConnectionRequestsTable/HederCell';
import { MaxWidthElipsisBox } from '../MaxWidthElipsisBox';

export const useColumns = ({
  isProvider,
  filters,
  onSetSort,
}: {
  isProvider: boolean;
  filters: BookingRequestsFiltersState;
  onSetSort: (data: SortDataPayload) => void;
}) => {
  const { formatCurrency } = useCurrencyFormatter();
  const DATE_FORMAT = 'LLL d, yyyy';
  return useMemo(() => {
    return [
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Code"
            sortKey="code"
          />
        ),
        id: 'code',
        accessorKey: 'code',
        size: 150,
        enableResizing: true,
        minSize: 100,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Facility"
            sortKey="healthcare_facility__name"
          />
        ),
        id: 'facility',
        accessorKey: 'healthcare_facility',
        cell: (props) => {
          const facility = props.getValue();

          return <div className="text-section-name--dark-brand">{facility.name}</div>;
        },
        size: 200,
        enableResizing: true,
        minSize: 100,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Sent by"
            sortKey="healthcare_entity_rep__user__last_name"
          />
        ),
        accessorKey: 'healthcare_entity_rep',
        cell: (props) => {
          const healthcareEntityRep = props.getValue();
          return (
            <div className="d-flex align-items-center">
              <Avatar className="me-1" size={24} />
              <span className="fs-14">
                {healthcareEntityRep.user.first_name} {healthcareEntityRep.user.last_name}
              </span>
            </div>
          );
        },
        size: 177,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Provider"
            sortKey="provider__last_name"
          />
        ),
        id: 'provider',
        accessorKey: 'provider',
        cell: (props) => {
          const provider = props.getValue();
          return (
            <div className="d-flex align-items-center text-section-name--dark-brand">
              <Avatar className="me-1" size={24} image={provider.profile_picture} />

              {provider.display_name}
            </div>
          );
        },
        size: 200,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Status"
            sortKey="status"
          />
        ),
        id: 'status',
        accessorFn: (row) => {
          return {
            status: row.status,
            bookings: row.bookings,
          };
        },
        cell: (props) => {
          const data = props.getValue();
          return <BookingRequestStatusBadge status={data.status} bookings={data.bookings} />;
        },
        size: 160,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="First Shift"
            sortKey="first_shift"
          />
        ),
        id: 'first_shift',
        accessorKey: 'bookings',
        cell: (props) => {
          const row = props.row.original;
          const shifts = props.getValue();

          if (row.status === BookingRequestReadStatusEnum.CANCELED) {
            return format(parseISO(shifts[0].start_on), DATE_FORMAT);
          }

          const firstNonRejectedShift = shifts.find(
            (shift) =>
              shift.status_by_provider !== StatusByProviderEnum.REJECTED &&
              shift.status_by_rep !== StatusByRepEnum.REJECTED
          );

          return firstNonRejectedShift ? (
            format(parseISO(firstNonRejectedShift.start_on), DATE_FORMAT)
          ) : (
            <span className="text-muted">-</span>
          );
        },
        size: 108,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Last Shift"
            sortKey="last_shift"
          />
        ),
        id: 'last_shift',
        accessorKey: 'bookings',
        cell: (props) => {
          const row = props.row.original;
          const shifts = props.getValue();

          if (row.status === BookingRequestReadStatusEnum.CANCELED) {
            return format(parseISO(shifts[shifts.length - 1].start_on), DATE_FORMAT);
          }

          const lastNonRejectedShift = [...shifts]
            .reverse()
            .find(
              (shift) =>
                shift.status_by_provider !== StatusByProviderEnum.REJECTED &&
                shift.status_by_rep !== StatusByRepEnum.REJECTED
            );

          return lastNonRejectedShift ? (
            format(parseISO(lastNonRejectedShift.start_on), DATE_FORMAT)
          ) : (
            <span className="text-muted">-</span>
          );
        },
        size: 108,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Shifts"
            sortKey="shift_count"
            className="text-end"
          />
        ),
        id: 'shift_count',
        accessorKey: 'shifts_count',
        cell: (props) => {
          return <div className="text-end">{props.getValue()}</div>;
        },
        size: 70,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Total Payment"
            sortKey="total_payment"
            className="text-end"
          />
        ),
        id: 'overall_total',
        accessorFn: (row) => {
          return row.overall_total;
        },
        cell: (props) => {
          return <div className="text-end">{formatCurrency(props.getValue())}</div>;
        },
        size: 108,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Specialty"
            sortKey="provider__specialties"
          />
        ),
        id: 'specialty',
        accessorKey: 'provider.specialties',
        cell: (props) => (
          <MaxWidthElipsisBox className="d-inline-block" maxWidth="300px">
            {props.getValue().join(', ')}
          </MaxWidthElipsisBox>
        ),
        size: 300,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Date Created"
            sortKey="created_at"
          />
        ),
        id: 'date_created',
        accessorKey: 'created_at',
        cell: (props) => {
          return <>{format(parseISO(props.getValue()), DATE_FORMAT)}</>;
        },
        size: 108,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Date Updated"
            sortKey="updated_at"
          />
        ),
        id: 'date_updated',
        accessorKey: 'updated_at',
        cell: (props) => {
          return <>{format(parseISO(props.getValue()), DATE_FORMAT)}</>;
        },
        size: 108,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
    ];
  }, [filters.sortBy, filters.sortDirection, isProvider, onSetSort]);
};
